class CartRecommendations extends HTMLElement {

	constructor(){
		super();
		this.loading = false;
		this.retryCount = 0;
		this.retryTimer = null;
		this.requestSerial = 0;
	}

	connectedCallback(){
		const drawer = this.closest('#site-cart-sidebar');
		if (!drawer || drawer.opened || drawer.classList.contains('sidebar--opened')) {
			this.generateRecommendations();
		}
	}

	async removeHiddenTaggedProducts(){
		const cards = Array.from(this.querySelectorAll('[data-js-product-item]'));
		if (cards.length === 0) return;

		window.ShopSanteRecommendationTagCache ||= new Map();
		const tagCache = window.ShopSanteRecommendationTagCache;
		const unresolvedCards = cards.filter(card => {
			const renderedTags = String(card.dataset.productTags || '').trim();
			const productId = card.id.match(/^product-item-(\d+)$/)?.[1];
			return !renderedTags && productId && !tagCache.has(productId);
		});

		if (unresolvedCards.length > 0) {
			const token = window.KROWN?.settings?.storefront_api_key;
			const domain = window.KROWN?.settings?.store_domain;
			const ids = unresolvedCards
				.map(card => card.id.match(/^product-item-(\d+)$/)?.[1])
				.filter(Boolean);

			if (token && domain && ids.length > 0) {
				try {
					const response = await fetch(`https://${domain}/api/2026-07/graphql.json`, {
						method: 'POST',
						headers: {
							'Content-Type': 'application/json',
							'X-Shopify-Storefront-Access-Token': token,
						},
						body: JSON.stringify({
							query: `query RecommendationTags($ids: [ID!]!) {
								nodes(ids: $ids) {
									... on Product { id tags }
								}
							}`,
							variables: { ids: ids.map(id => `gid://shopify/Product/${id}`) },
						}),
					});

					if (response.ok) {
						const json = await response.json();
						(json?.data?.nodes || []).forEach(product => {
							const productId = String(product?.id || '').split('/').pop();
							if (productId) {
								tagCache.set(productId, Array.isArray(product.tags)
									? product.tags.map(tag => String(tag).trim().toLowerCase())
									: []);
							}
						});
					}
				} catch (error) {
					console.warn('Unable to verify recommendation tags through Storefront API.', error);
				}
			}
		}

		await Promise.all(cards.map(async card => {
			const renderedTags = String(card.dataset.productTags || '')
				.split('|')
				.map(tag => tag.trim().toLowerCase())
				.filter(Boolean);
			let tags = renderedTags;

			if (tags.length === 0) {
				const productId = card.id.match(/^product-item-(\d+)$/)?.[1];
				const handle = card.dataset.jsProductItem;
				if (!handle) return;
				if (productId && tagCache.has(productId)) {
					tags = tagCache.get(productId);
				} else {
					if (!tagCache.has(handle)) {
						const rootUrl = window.Shopify?.routes?.root || '/';
						tagCache.set(handle, fetch(`${rootUrl}products/${encodeURIComponent(handle)}.js`, {
							cache: 'no-store',
							credentials: 'same-origin',
							headers: { 'Accept': 'application/json' },
						})
							.then(response => response.ok ? response.json() : null)
							.then(product => Array.isArray(product?.tags)
								? product.tags.map(tag => String(tag).trim().toLowerCase())
								: [])
							.catch(() => []));
					}

					tags = await tagCache.get(handle);
				}
			}

			if (tags.includes('hide')) card.remove();
		}));

		if (this.querySelectorAll('[data-js-product-item]').length === 0) {
			this.innerHTML = '';
		}
	}

	disconnectedCallback(){
		clearTimeout(this.retryTimer);
		this.retryTimer = null;
		this.requestSerial += 1;
	}

	scheduleRetry(){
		const retryDelays = [400, 1000, 2200];
		if (!this.isConnected || this.retryCount >= retryDelays.length) {
			console.warn('Cart recommendations unavailable after automatic retries.');
			return;
		}

		const delay = retryDelays[this.retryCount];
		this.retryCount += 1;
		clearTimeout(this.retryTimer);
		this.retryTimer = setTimeout(() => {
			this.retryTimer = null;
			this.generateRecommendations();
		}, delay);
	}

	async generateRecommendations(){
		if (this.loading) return;
		if (this.dataset.loaded === 'true') {
			await this.removeHiddenTaggedProducts();
			return;
		}

		const cartForm = document.getElementById('AjaxCartForm');
		if (!cartForm) return;

		const cartItems = cartForm.querySelectorAll('[data-js-cart-item]');
		if ( cartItems.length > 0 ) {
			const cartItem = Array.from(cartItems).find(product => product.getAttribute('data-price') != '0');
			if (!cartItem?.dataset?.productId) return;

			clearTimeout(this.retryTimer);
			this.retryTimer = null;
			this.loading = true;
			const requestId = ++this.requestSerial;
			const controller = new AbortController();
			const timeout = setTimeout(() => controller.abort(), 9000);
			const url = new URL(KROWN.settings.routes.product_recommendations_url, window.location.origin);
			url.searchParams.set('section_id', this.dataset.section);
			url.searchParams.set('product_id', cartItem.dataset.productId);
			url.searchParams.set('limit', this.dataset.limit);
			url.searchParams.set('_cart_recommendations_revision', '20260908-native-stock');

			try {
				const response = await fetch(url.toString(), {
					cache: 'no-store',
					credentials: 'same-origin',
					headers: { 'X-Requested-With': 'XMLHttpRequest' },
					signal: controller.signal,
				});
				if (!response.ok) throw new Error(`Recommendation request failed (${response.status})`);

				const text = await response.text();
				if (requestId !== this.requestSerial || !this.isConnected) return;

				const innerHTML = new DOMParser()
					.parseFromString(text, 'text/html')
					.querySelector('[data-js-cart-recommendations-performed]');
				this.innerHTML = '';
				if (innerHTML && innerHTML.querySelectorAll('[data-js-product-item]').length > 0) {
					this.innerHTML = innerHTML.innerHTML;
					await this.removeHiddenTaggedProducts();
				}

				this.dataset.loaded = 'true';
				this.retryCount = 0;
				const recommendationSelector = this.id ? `#${this.id}` : 'cart-recommendations';
				if (typeof cartSliderFormSubmit === 'function') cartSliderFormSubmit(`${recommendationSelector} form.upsell`, false);
				if (typeof cartSliderSelect === 'function') cartSliderSelect(`${recommendationSelector} form.upsell select`);
				if (window.swat) window.swat.initializeActionButtons('body');
				this.dispatchEvent(new Event('cart-recommendations-loaded'));
			} catch (error) {
				if (requestId === this.requestSerial) {
					this.dataset.loaded = 'false';
					this.scheduleRetry();
				}
			} finally {
				clearTimeout(timeout);
				if (requestId === this.requestSerial) this.loading = false;
			}
		}
	}
}
if (!customElements.get('cart-recommendations')) {
	customElements.define('cart-recommendations', CartRecommendations);
}
